import React from 'react';

import { cn } from '@/lib/utils';

interface UsageMeterProps {
  label: string;
  current: number;
  limit: number; // -1 = unlimited
  size?: 'sm' | 'md';
}

const SEGMENTS = 28;

/**
 * A segmented usage meter: a row of thin bars that fill to the consumed portion
 * of a plan limit. Monochrome — filled segments use the primary text colour so it
 * stays black-on-light / light-on-dark. Unlimited limits show a faint full bar.
 */
export const UsageMeter: React.FC<UsageMeterProps> = ({ label, current, limit, size = 'md' }) => {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const filled = isUnlimited ? 0 : current > 0 ? Math.max(1, Math.round((pct / 100) * SEGMENTS)) : 0;

  const barHeight = size === 'sm' ? 'h-[18px]' : 'h-[22px]';
  const labelSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className={cn('min-w-0 truncate font-medium', labelSize)}>{label}</span>
        <span
          className={cn('shrink-0 font-medium', labelSize, isUnlimited && 'text-muted-foreground')}
        >
          {isUnlimited ? 'Unlimited' : `${Math.round(pct)}%`}
        </span>
      </div>

      {isUnlimited ? (
        <div className={cn('bg-muted', barHeight)} />
      ) : (
        <div className={cn('flex items-stretch gap-[3px]', barHeight)}>
          {Array.from({ length: SEGMENTS }).map((_, i) => (
            <div
              key={i}
              className={cn('flex-1 transition-colors', i < filled ? 'bg-foreground' : 'bg-border')}
            />
          ))}
        </div>
      )}

      <p className="mt-1 text-[11px] text-muted-foreground">
        {isUnlimited ? `${current.toLocaleString()} used` : `${current.toLocaleString()} / ${limit.toLocaleString()}`}
      </p>
    </div>
  );
};
